import { type RefObject, useEffect } from "react";
import { canScrollVerticallyWithin } from "web_myplann/Shared/Utils/canScrollVerticallyWithin";
import { getCoveredBottom } from "web_myplann/Shared/Utils/getCoveredBottom";
import { getPaintedContentBottom } from "web_myplann/Shared/Utils/getPaintedContentBottom";
import { observeScrollerContent } from "web_myplann/Shared/Utils/observeScrollerContent";

const MAX_TRAILING_WHITESPACE = 64;

export const usePreventMicroScroll = (
	scrollerRef: RefObject<HTMLElement | null>,
	enabled: boolean,
): void => {
	useEffect(() => {
		const scroller = scrollerRef.current;
		if (!enabled || !scroller) return;

		let locked = false;
		let start = { x: 0, y: 0 };

		const handleTouchStart = ({ touches }: TouchEvent) => {
			start = { x: touches[0].clientX, y: touches[0].clientY };
		};

		const handleTouchMove = (event: TouchEvent) => {
			const [touch] = event.touches;
			const isVertical =
				Math.abs(touch.clientY - start.y) >= Math.abs(touch.clientX - start.x);
			if (
				event.cancelable &&
				event.touches.length === 1 &&
				isVertical &&
				!canScrollVerticallyWithin(event.target, scroller)
			) {
				event.preventDefault();
			}
		};

		const keepAtTop = () => {
			if (scroller.scrollTop !== 0) {
				scroller.scrollTop = 0;
			}
		};

		const setLocked = (next: boolean) => {
			if (next === locked) return;
			locked = next;

			if (next) {
				scroller.addEventListener("touchmove", handleTouchMove, {
					passive: false,
				});
				scroller.addEventListener("scroll", keepAtTop, { passive: true });
				keepAtTop();
			} else {
				scroller.removeEventListener("touchmove", handleTouchMove);
				scroller.removeEventListener("scroll", keepAtTop);
			}
		};

		const update = () => {
			const overflow = scroller.scrollHeight - scroller.clientHeight;
			const padding = Number.parseFloat(
				window.getComputedStyle(scroller).paddingBottom,
			);
			setLocked(
				overflow > 0 &&
					overflow <= padding + MAX_TRAILING_WHITESPACE &&
					getPaintedContentBottom(scroller) <=
						scroller.clientHeight - getCoveredBottom(scroller),
			);
		};

		scroller.addEventListener("touchstart", handleTouchStart, {
			passive: true,
		});
		const stopObserving = observeScrollerContent(scroller, update);
		const insetObserver = new MutationObserver(update);
		insetObserver.observe(document.documentElement, {
			attributeFilter: ["style"],
		});
		insetObserver.observe(scroller, { attributeFilter: ["class"] });

		return () => {
			stopObserving();
			insetObserver.disconnect();
			scroller.removeEventListener("touchstart", handleTouchStart);
			setLocked(false);
		};
	}, [enabled, scrollerRef]);
};
